// `capture_sessions` DELETE dispatch for cancelSession.
//
// The PowerSync adapter (capture-client-powersync-cancel-session.ts)
// DELETEs the local row + its capture_items immediately so the dialog
// can close offline; this connector entry POSTs DELETE
// /capture/sessions/:id?syncVersion=N to actually remove the session
// on the server when connectivity returns.
//
// Discriminator note: the connector's capture_sessions DELETE branch
// is also used as the orphan-drop ack for the createSession 409
// handler (connector-dispatch-session-insert.ts) — that path DELETEs
// the local row when a multi-device race rejects the optimistic
// session-create. We deliberately POST DELETE in both cases:
//   - cancel: server actually has the session, returns 200 + recomputed
//     buildingStatus; we ack and move on (the buildings sync stream
//     will deliver the authoritative status separately).
//   - orphan-drop: server never accepted the session, returns 404; we
//     treat that as idempotent success (one extra HTTP round-trip is
//     cheaper than threading a discriminator through the SDK's CrudEntry
//     shape, which has no metadata channel for "user intent").
//
// RE-HOMED 404 — the H2/#700 carve-out (do NOT over-cancel). The 404 above
// has a THIRD shape: entry.id is a local orphan whose createSession INSERT
// 409'd and was RE-HOMED onto an authoritative session Y
// (connector-dispatch-session-insert.ts records orphan→Y in the durable
// session re-home registry, then DELETEs the orphan). Its children were
// merged onto Y, so Y carries counts BEYOND this orphan's — another device's
// in-flight session, or a completed building. A cancel is a WHOLE-SESSION
// destructive delete (capture_items cascade away via the FK), NOT a
// per-row/per-increment op like the capture_items re-home redirect. So
// redirecting THIS delete onto Y (the naive "mirror item-patch and redirect"
// fix) would cascade-delete Y's OTHER counts — the inverse data-loss, far
// worse than losing one cancel. We therefore consult getSessionRehome BEFORE
// treating the 404 as a plain success: if the session was re-homed we
// PRESERVE Y (never redirect the destructive delete), surface the event
// (Sentry + console — never silent, the bug this fixes), and ack to drain.
// We deliberately do NOT emit `uploaded`: nothing was cancelled server-side,
// so a success signal would be a lie (same stance as the capture_sessions
// PATCH not_found-ack in connector-dispatch-session.ts). Both a genuine
// surveyor cancel of a re-homed orphan AND the connector's own post-re-home
// orphan-drop reach here as a byte-identical DELETE; both demand this
// identical safe handling, so we don't try to distinguish them. The "never
// silently discard a count that wasn't explicitly cancelled" rule wins over
// honouring the cancel (the surveyor still sees Y on the next sync and can
// re-cancel it directly). A surveyor-facing "your cancel didn't apply" toast
// would need a cancel-intent tombstone (the pending_space_deletes pattern) to
// tell a real cancel from the orphan-drop — deferred as a follow-up; the
// telemetry above closes the silent-loss gap without that surface.
//
// 409 handling: a `failed_precondition` response means the cancel was
// REJECTED at the backend's version guard — the server still has the
// session (e.g. another device completed-then-reopened it). The
// previous version of this dispatch acked the 409 as idempotent
// success, which silently lost the user's intent: the local DELETE
// drained from the queue, but the captures sync stream would deliver
// the still-active session back to the local mirror, and the UI would
// show it as active again (Codex P2 on PR #542).
//
// Correct behaviour: refetch the session's current syncVersion via
// GET /capture/sessions/:id (a SessionDetail — the version is NESTED at
// `.session.syncVersion`, NOT a flat top-level field) and retry the
// DELETE once with the fresh guard. The retry typically succeeds (no
// further races); if it 404s
// the session was cancelled by someone else in the meantime (success);
// if it 409s again we rethrow so the SDK keeps the entry queued for
// the next upload cycle — at that point a fresher captures sync
// should have surfaced the version that's actually current.
//
// Why not throw immediately on first 409:
//   The SDK retries the WHOLE batch when uploadData rejects, and the
//   queued DELETE's previousValues carry FROZEN sync_version — the
//   retry would fail with the same 409 forever, wedging every other
//   entry in the batch (createItem PATCHes, etc.). Refetch-and-retry
//   keeps the queue draining in the common-case race.

import type { CrudEntry } from '@powersync/web'
import * as Sentry from '@sentry/react'

import type { SessionDetail } from '@citycare/shared'

import { ApiError, apiFetch } from '../api'

import { getSessionRehome } from './connector-session-rehome-registry'
import { _emitUploaded } from './upload-events'

async function deleteWithVersion(
  sessionId: string,
  syncVersion: number,
): Promise<void> {
  await apiFetch(
    `/capture/sessions/${encodeURIComponent(sessionId)}?syncVersion=${syncVersion}`,
    { method: 'DELETE' },
  )
}

/**
 * H2/#700: handle a capture_sessions DELETE that 404'd because its session was
 * RE-HOMED onto an authoritative target Y. Returns true when it handled the
 * entry (the caller must ack by returning without emitting `uploaded`); false
 * when the session was never re-homed and the caller should fall through to the
 * plain idempotent-success path.
 *
 * A cancel is a WHOLE-SESSION cascade delete, so the local orphan's children
 * were merged onto Y and Y carries counts beyond this orphan's. Redirecting the
 * delete onto Y would cascade-delete those too (inverse data-loss), so we never
 * redirect: we PRESERVE Y and surface the dropped delete (Sentry + console) so
 * it is never silent. See the file header for the full contract.
 */
function preserveIfRehomed(entry: CrudEntry): boolean {
  const rehome = getSessionRehome(entry.id)
  if (rehome === undefined) return false
  Sentry.captureMessage(
    '[powersync] capture_sessions DELETE 404 on a re-homed session — preserving the authoritative session, not over-cancelling',
    {
      level: 'warning',
      extra: {
        reason: 'powersync_session_cancel_rehomed_preserve',
        entryId: entry.id,
        authoritativeId: rehome.authoritativeId,
      },
    },
  )
  console.warn(
    '[powersync] capture_sessions DELETE 404 on a re-homed session — not ' +
      'over-cancelling the authoritative session it merged onto',
    { id: entry.id, authoritativeId: rehome.authoritativeId },
  )
  return true
}

export async function dispatchSessionCancel(entry: CrudEntry): Promise<void> {
  const previousValues = entry.previousValues ?? {}
  const syncVersion = previousValues['sync_version']

  // The schema's `trackPrevious: { columns: ['completed_at', 'sync_version'] }`
  // populates previousValues on every capture_sessions DELETE — the row
  // existed before the delete by definition. If it's missing we throw
  // rather than guessing: an unguarded DELETE would bypass the
  // backend's applyVersionGuard and let stale-version writes through.
  if (typeof syncVersion !== 'number') {
    throw new Error(
      `dispatchSessionCancel: capture_sessions DELETE ${entry.id} is missing ` +
        `sync_version in previousValues — the schema's trackPrevious config ` +
        `must populate it. Without the guard the backend can't reject stale ` +
        `cancels and the LWW contract breaks.`,
    )
  }

  try {
    await deleteWithVersion(entry.id, syncVersion)
    _emitUploaded(entry.table, entry.id)
    return
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // RE-HOMED orphan? Then this delete can NEVER over-cancel the
      // authoritative session it merged onto. Preserve + surface, do NOT
      // emit `uploaded`. See the file header (H2/#700).
      if (preserveIfRehomed(entry)) return
      // Not re-homed: orphan bare-drop (no authoritative to re-home onto) or
      // a concurrent cancel from another device. The session is genuinely
      // gone server-side and carried no re-homed counts — idempotent success.
      _emitUploaded(entry.table, entry.id)
      return
    }
    if (err instanceof ApiError && err.code === 'failed_precondition') {
      // Refetch authoritative syncVersion + retry once. Backend
      // message on 409 literally says "refetch and retry".
      await retryCancelWithFreshVersion(entry)
      return
    }
    throw err
  }
}

async function retryCancelWithFreshVersion(entry: CrudEntry): Promise<void> {
  let fresh: SessionDetail
  try {
    fresh = await apiFetch<SessionDetail>(
      `/capture/sessions/${encodeURIComponent(entry.id)}`,
      { method: 'GET' },
    )
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Re-homed orphan? Preserve the authoritative session, never emit a
      // false `uploaded`. Structural, not reachability-dependent: a re-homed
      // orphan 404s on the FIRST delete and never reaches this 409-retry path
      // today (the version guard only runs for a session that EXISTS
      // server-side), but guarding here keeps all three 404-ack sites uniform
      // if the backend's 404-vs-409 shape ever shifts (audit-1 #1).
      if (preserveIfRehomed(entry)) return
      // Session disappeared between the cancel attempt and the refetch
      // — someone else cancelled it. User intent met.
      _emitUploaded(entry.table, entry.id)
      return
    }
    throw err
  }
  // GET /capture/sessions/:id returns a SessionDetail: the syncVersion is
  // NESTED at `.session.syncVersion`. Reading a flat `fresh.syncVersion`
  // here yielded `undefined`, so this guard threw, uploadData rethrew, and
  // the SDK retried the whole batch forever — wedging every queued
  // mutation behind it (C3 / #691). The `?.` keeps a malformed body a
  // descriptive throw rather than a raw TypeError.
  if (typeof fresh.session?.syncVersion !== 'number') {
    throw new Error(
      `dispatchSessionCancel: GET /capture/sessions/${entry.id} did not ` +
        `return session.syncVersion — can't retry the cancel with a fresh guard.`,
    )
  }
  try {
    await deleteWithVersion(entry.id, fresh.session.syncVersion)
    _emitUploaded(entry.table, entry.id)
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Re-homed orphan? Preserve + surface (mirrors the GET-404 arm above; the
      // same structural-uniformity guard — audit-1 #1).
      if (preserveIfRehomed(entry)) return
      // Cancelled by someone else between refetch and retry.
      _emitUploaded(entry.table, entry.id)
      return
    }
    // A second 409 (sustained race) or any other error: rethrow so the
    // SDK keeps the entry queued; the next upload tick will retry the
    // refetch with a yet-fresher version.
    throw err
  }
}
